import {
  AuditEventRow,
  CloudAuditQueryRow,
  GetSessionsResponseBody,
} from "@p0security/shared/integrations/sessions/types";
import { App } from "antd";
import { ExportOption } from "components/Export";
import { useAuthFetch } from "components/Login/hook";
import { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";

type SuccessfulSessionsResponse = Extract<
  GetSessionsResponseBody,
  { ok: true }
>;

const CLOUD_AUDIT_CSV_COLUMNS: (keyof CloudAuditQueryRow)[] = [
  "requestId",
  "eventId",
  "eventTime",
  "eventName",
  "eventSource",
  "region",
  "errorCode",
  "ipAddress",
  "userAgent",
  "principal",
  "resources",
  "requestParameters",
];

const AUDIT_EVENT_CSV_COLUMNS: (keyof AuditEventRow)[] = [
  "id",
  "timestamp",
  "severity",
  "action",
  "actor",
  "target",
  "summary",
  "details",
  "raw",
];

const escapeExportValue = (value: unknown) => {
  if (value == null) return "";
  if (typeof value === "object") return `"${JSON.stringify(value)}"`;
  return `"${String(value).replace(/"/g, '""')}"`;
};

const toCsv = <T extends Record<string, unknown>>(
  rows: T[],
  columns: readonly (keyof T & string)[]
): string => {
  if (!rows.length) return "";
  let result = columns.join(",") + "\n";
  for (const row of rows) {
    result +=
      columns.map((column) => escapeExportValue(row[column])).join(",") + "\n";
  }
  return result;
};

const toResponseCsv = (nextResponse: SuccessfulSessionsResponse): string => {
  if (nextResponse.view.kind === "cloud-audit") {
    return toCsv(nextResponse.view.rows, CLOUD_AUDIT_CSV_COLUMNS);
  }
  if (nextResponse.view.kind === "audit-events") {
    return toCsv(nextResponse.view.rows, AUDIT_EVENT_CSV_COLUMNS);
  }
  return "";
};

const toResponseJson = (nextResponse: SuccessfulSessionsResponse): string =>
  JSON.stringify(nextResponse.view, null, 2);

export function useSessionHistory(requestId: string) {
  const { message } = App.useApp();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [search, setSearch] = useState("");
  const [includeResourceSearch, setIncludeResourceSearch] = useState(false);
  const [response, setResponse] = useState<
    SuccessfulSessionsResponse | undefined
  >(undefined);
  const authFetch = useAuthFetch(setError, setLoading);
  const inFlightRef = useRef(false);

  const view = response?.view;

  const updateQuantity = useCallback((value: number) => {
    setQuantity(value);
  }, []);

  const updateSearch = useCallback((value: ChangeEvent<HTMLInputElement>) => {
    setSearch(value.target.value);
  }, []);

  const updateIncludeResourceSearch = useCallback((value: boolean) => {
    setIncludeResourceSearch(value);
  }, []);

  const getSession = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    try {
      const params = new URLSearchParams();
      params.set("limit", quantity.toString());
      if (search.trim()) {
        params.set("search", search);
        if (includeResourceSearch) {
          params.set("includeResourceSearch", "true");
        }
      }
      const result = await authFetch(
        `sessions/${requestId}?${params.toString()}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!result) {
        setError("Response is empty");
        return;
      }
      if (!result.ok) {
        setError("Response failed");
        return;
      }
      const json: GetSessionsResponseBody = await result.json();
      if (!json.ok) {
        setError(json.error);
        return;
      }
      setError(undefined);
      setResponse(json);
    } finally {
      inFlightRef.current = false;
    }
  }, [authFetch, quantity, requestId, search, includeResourceSearch]);

  // Async export for all (limit 500)
  const fetchAllResponse = useCallback(async (): Promise<
    SuccessfulSessionsResponse | undefined
  > => {
    const params = new URLSearchParams();
    params.set("limit", "500");
    const result = await authFetch(
      `sessions/${requestId}?${params.toString()}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!result) {
      message.error("Error: No response from server");
      return undefined;
    }
    if (!result.ok) {
      message.error("Error: Failed to fetch all session history");
      return undefined;
    }
    const json: GetSessionsResponseBody = await result.json();
    if (!json.ok) {
      message.error(json.error || "Unknown error fetching all session history");
      return undefined;
    }
    return json;
  }, [authFetch, message, requestId]);

  const exportOptions = useMemo<Record<string, ExportOption<unknown>>>(
    () => ({
      csv: {
        label: "CSV (current request)",
        blob: async () => (response ? toResponseCsv(response) : ""),
        extension: "csv",
      },
      json: {
        label: "JSON (current request)",
        blob: async () => (response ? toResponseJson(response) : ""),
        extension: "json",
      },
      csvAll: {
        label: "CSV (all, max 500)",
        blob: async () => {
          const allResponse = await fetchAllResponse();
          return allResponse ? toResponseCsv(allResponse) : "";
        },
        extension: "csv",
      },
      jsonAll: {
        label: "JSON (all, max 500)",
        blob: async () => {
          const allResponse = await fetchAllResponse();
          return allResponse ? toResponseJson(allResponse) : "";
        },
        extension: "json",
      },
    }),
    [fetchAllResponse, response]
  );

  return {
    view,
    error,
    loading,
    quantity,
    updateQuantity,
    search,
    updateSearch,
    includeResourceSearch,
    updateIncludeResourceSearch,
    getSession,
    exportOptions,
    requestId,
  };
}
